import { ApiError } from './errors';
import { handleMockRequest } from './mock';

export interface ApiClientConfig {
  baseUrl: string;
  getToken?: () => Promise<string | null>;
  mockEnabled?: boolean;
  /** Mock delay in ms (default 300-500). Set to 0 in tests to skip delay. */
  mockDelayMs?: number;
}

export interface ApiClient {
  get: <T>(endpoint: string) => Promise<T>;
  post: <T>(endpoint: string, body: unknown) => Promise<T>;
  patch: <T>(endpoint: string, body: unknown) => Promise<T>;
  delete: <T>(endpoint: string) => Promise<T>;
  request: <T>(endpoint: string, options?: RequestInit) => Promise<T>;
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  const { baseUrl, getToken } = config;

  async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Use mock data in mock mode
    if (config.mockEnabled) {
      // prettier-ignore
      const delay = config.mockDelayMs ?? (300 + Math.random() * 200);
      if (delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
      return handleMockRequest<T>(endpoint, options);
    }

    const token = getToken ? await getToken() : null;

    const method = options.method?.toUpperCase();
    const hasBody = method === 'POST' || method === 'PATCH' || method === 'PUT';

    const headers: Record<string, string> = {
      ...(hasBody && { 'Content-Type': 'application/json' }),
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${baseUrl}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...(options.headers as Record<string, string>),
      },
    });

    // Handle rate limiting
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After') || '60';
      throw new ApiError(
        `Rate limit exceeded. Please try again in ${retryAfter} seconds.`,
        429,
        'RATE_LIMITED'
      );
    }

    // Handle successful responses with no content
    if (response.status === 204) {
      return undefined as T;
    }

    // Parse response body
    const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;

    // Handle error responses
    if (!response.ok) {
      const errorObj = data.error as Record<string, unknown> | undefined;
      throw new ApiError(
        (errorObj?.message as string) ||
          (data.message as string) ||
          `API Error: ${response.status}`,
        response.status,
        errorObj?.code as string | undefined
      );
    }

    return data as T;
  }

  return {
    request,
    get: <T>(endpoint: string) => request<T>(endpoint, { method: 'GET' }),
    post: <T>(endpoint: string, body: unknown) =>
      request<T>(endpoint, { method: 'POST', body: JSON.stringify(body) }),
    patch: <T>(endpoint: string, body: unknown) =>
      request<T>(endpoint, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: <T>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),
  };
}
